/**
 * The campaign funnel — PRD §1–§3.
 *
 * One component for all three destinations. §1 forbids a selector and any link
 * between the variants, so the ROUTE fixes the audience and the words come
 * from `audienceCopy`; nothing here branches on which campaign this is.
 *
 * State lives in the URL-less session token rather than in React state alone:
 * a visitor who refreshes mid-wait, or comes back in an hour, resumes the same
 * visit rather than starting a second bucket.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../app/http-common';
import { useConfig } from '../../contexts/FrontendConfigContext';
import { copyFor, isAudience, SHARED } from './audienceCopy';
import CampaignShell from './CampaignShell';
import LandingStep, { LandingCapabilities } from './LandingStep';
import { EVENTS, track, trackStage } from '../../analytics/campaignAnalytics';
import { isMockup, resolveVariant } from './campaignVariant';
import MockShell from './mockup/MockShell';
import MockLanding from './mockup/MockLanding';
import MockWaiting from './mockup/MockWaiting';
import MockList from './mockup/MockList';
import MockReportWait from './mockup/MockReportWait';
import MockEnroll from './mockup/MockEnroll';
import MockWorkspace from './mockup/MockWorkspace';
import WaitingStep from './WaitingStep';
import ListStep from './ListStep';
import ReportWaitStep from './ReportWaitStep';
import ReportView from './ReportView';
import EnrollStep from './EnrollStep';
import WorkspaceStep from './WorkspaceStep';
import './Campaign.css';

const TOKEN_KEY = 'alchemiq.campaign.session';
const BRIEF_KEY = 'alchemiq.campaign.brief';
const QUESTIONS_KEY = 'alchemiq.campaign.questions';
const POLL_MS = 4000;

/**
 * localStorage, not sessionStorage: the whole point of the token is that
 * closing the tab does not lose the list and the research behind it. Scoped
 * per audience so an internal reviewer flipping between the three preview
 * links does not have one variant resume into another's visit.
 */
const tokenKeyFor = (audience) => `${TOKEN_KEY}.${audience}`;
const briefKeyFor = (audience) => `${BRIEF_KEY}.${audience}`;
const questionsKeyFor = (audience) => `${QUESTIONS_KEY}.${audience}`;

function readStored(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function writeStored(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* private mode — the visit still exists server-side */ }
}

/** §1 — recorded, never used to choose the copy. */
function readUtm() {
  try {
    const q = new URLSearchParams(window.location.search);
    const out = {};
    for (const name of ['utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'utm_term', 'utm_creative']) {
      const v = q.get(name);
      if (v) out[name] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * A campaign visitor has no account, so `?s=<sessionToken>` is the only way an
 * email can put them back in their own workspace — which Q1's reminder and
 * Q6's curated-email links both require ("returns to the actual unfinished
 * step and preserves inputs").
 *
 * The token is adopted into localStorage and then REMOVED from the address
 * bar. It is a bearer credential: leaving it in the URL puts it in the
 * browser history, in the Referer header of every outbound link on the page,
 * and in anything the visitor copies and pastes to a colleague.
 *
 * A link token wins over a stored one. Someone who followed their own email
 * is telling us which visit they mean, and the stored token may be an older
 * brief from the same browser.
 */
function adoptLinkToken(audience) {
  const stored = readStored(tokenKeyFor(audience));
  try {
    const url = new URL(window.location.href);
    const linked = url.searchParams.get('s');
    if (!linked) return stored;
    writeStored(tokenKeyFor(audience), linked);
    url.searchParams.delete('s');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    return linked;
  } catch {
    return stored;
  }
}

export default function CampaignPage() {
  const { audience: routeAudience } = useParams();
  const config = useConfig();
  // §1 — the route decides. An unknown path segment is not silently coerced
  // into a default variant; it is not a campaign destination at all.
  const audience = isAudience(routeAudience) ? routeAudience : null;

  // Resolved once per mount. `?v=` wins and is remembered, so navigating to
  // FAQ or Pricing cannot drop a visitor into the other version mid-journey.
  const [variant] = useState(() => resolveVariant());
  const mock = isMockup(variant);

  // Same state machine, two presentations. Every handler, guard and piece
  // of copy below is shared; only the components that render them differ,
  // which is what keeps the two designs from drifting in BEHAVIOUR while
  // they differ in look.
  const Shell = mock ? MockShell : CampaignShell;
  const Landing = mock ? MockLanding : LandingStep;
  const Waiting = mock ? MockWaiting : WaitingStep;
  const List = mock ? MockList : ListStep;
  const ReportWait = mock ? MockReportWait : ReportWaitStep;
  const Enroll = mock ? MockEnroll : EnrollStep;
  const Workspace = mock ? MockWorkspace : WorkspaceStep;
  // The few things this component renders itself rather than delegating to
  // a step — the closed notice, the upgrade prompt, the revise action.
  // Listed explicitly rather than prefixed with `${design}-`: the two
  // designs do not have a class for class, and a computed name that does
  // not exist fails silently as unstyled markup.
  const C = mock ? {
    closed: 'mk-closed',
    notice: 'mk-notice',
    noticeTitle: 'mk-h2',
    noticeBody: 'mk-body',
    actions: 'mk-actions',
    quietBtn: 'mk-btn mk-btn--ghost',
    primaryBtn: 'mk-btn mk-btn--primary',
  } : {
    closed: 'cmp-closed',
    notice: 'cmp-notice',
    noticeTitle: 'cmp-section-title',
    noticeBody: 'cmp-faq-answer',
    actions: 'cmp-actions',
    quietBtn: 'cmp-btn cmp-btn--quiet',
    primaryBtn: 'cmp-btn cmp-btn--primary',
  };

  const [token, setToken] = useState(() => (audience ? adoptLinkToken(audience) : null));
  // §1 — "Preserve what they typed when navigating back."
  const [brief, setBrief] = useState(() => (audience ? readStored(briefKeyFor(audience)) || '' : ''));
  const [visit, setVisit] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // How far through the optional questions this visitor has got. Persisted
  // because it is not on the server: the backend records ANSWERS, and a
  // skip is not an answer, so a refresh after skipping them all used to
  // put the whole set back in front of someone who had already dismissed
  // it. §5 makes them optional — asking twice makes them feel compulsory.
  const [questionIndex, setQuestionIndex] = useState(
    () => (audience ? Number(readStored(questionsKeyFor(audience))) || 0 : 0),
  );
  // The visitor's own choice to leave the waiting screen. Kept separate from
  // the server state so the list-ready panel does not yank them out of a
  // question they are halfway through answering.
  const [viewingList, setViewingList] = useState(false);
  // Whether they have ever opened it. "Back to my opportunities" is a lie
  // before the first time — the list exists, but they have not been there, so
  // the header would be offering to return them somewhere they have not been.
  const [hasSeenList, setHasSeenList] = useState(false);
  // §3 — which opportunity's research the visitor is looking at, and whether
  // they have chosen to open it. `null` means they are on the list.
  const [activeReport, setActiveReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  // Q6/Q13 — registration happens beside the first report, and the workspace
  // is where they land afterwards and every time they come back.
  const [enrolling, setEnrolling] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const [viewingWorkspace, setViewingWorkspace] = useState(false);
  // Q14/Q17 — which upgrade prompt to show, if either.
  const [upgradePrompt, setUpgradePrompt] = useState(null);

  const pollTimer = useRef(null);
  const reportTimer = useRef(null);
  // Which report the poll is currently following. A response that arrives
  // for any other one — including none, after Back — is discarded.
  const watchedReport = useRef(null);
  const cancelled = useRef(false);
  // Whether this mount has already seen a poll result. A visitor returning to
  // a list that finished long ago is not waiting for anything.
  const firstPoll = useRef(true);

  useEffect(() => () => {
    cancelled.current = true;
    if (pollTimer.current) clearTimeout(pollTimer.current);
    if (reportTimer.current) clearTimeout(reportTimer.current);
  }, []);

  useEffect(() => {
    if (audience) writeStored(briefKeyFor(audience), brief || null);
  }, [audience, brief]);

  useEffect(() => {
    if (audience) writeStored(questionsKeyFor(audience), questionIndex ? String(questionIndex) : null);
  }, [audience, questionIndex]);

  /**
   * One poll at a time, rescheduled from its own completion rather than on an
   * interval: a slow response must not stack requests behind it, which on a
   * public endpoint with a rate limit is how a waiting screen locks itself out.
   */
  const poll = useCallback(async (sessionToken) => {
    if (cancelled.current || !sessionToken) return;
    try {
      const next = await api.getCampaignVisit(sessionToken);
      if (cancelled.current) return;
      setVisit(next);
      // Land a returning visitor on their list, not on the waiting screen.
      // "We're finding stories for your coverage" is false once the list
      // exists, and §2 is strict that the activity state must be truthful —
      // the waiting screen is for people who are actually waiting.
      if (firstPoll.current) {
        firstPoll.current = false;
        if (next.state === 'ready') { setViewingList(true); setHasSeenList(true); }
      }
      if (next.brief && !brief) setBrief(next.brief);
      // `delayed` keeps polling on purpose (Q3): the run is alive past five
      // minutes and, if it lands, the screen must switch to ready straight
      // away rather than sit on a stale delay message.
      const settled = ['ready', 'empty', 'failed'].includes(next.state);
      if (!settled) {
        pollTimer.current = setTimeout(() => poll(sessionToken), POLL_MS);
      }
    } catch (err) {
      if (cancelled.current) return;
      if (err.response?.status === 404) {
        // The visit is gone — an environment reset, most likely. Forget the
        // token and let the visitor start again rather than polling forever.
        writeStored(tokenKeyFor(audience), null);
        setToken(null);
        setVisit(null);
        return;
      }
      // Anything else is probably transient. Keep asking; the waiting screen
      // is already telling the truth about what it knows.
      pollTimer.current = setTimeout(() => poll(sessionToken), POLL_MS * 2);
    }
  }, [audience, brief]);

  useEffect(() => {
    if (!token) return undefined;
    poll(token);
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
    // `poll` is intentionally not a dependency: it closes over `brief`, which
    // changes as the visit loads, and re-running this would start a second
    // poll chain against the same token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitBrief = useCallback(async () => {
    setError(null);
    if (!String(brief || '').trim()) {
      setError(SHARED.emptyBriefError);
      return;
    }
    setSubmitting(true);
    try {
      track(EVENTS.BRIEF_SUBMIT, { audience, brief_length: String(brief || '').length });
      const out = await api.startCampaignVisit({
        audience, brief, utm: readUtm(), landingVariant: variant,
      });
      writeStored(tokenKeyFor(audience), out.sessionToken);
      firstPoll.current = true;
      setToken(out.sessionToken);
      setVisit({ state: 'building', brief, opportunities: [], audience });
      // A new brief is a new run, so the questions start again.
      setQuestionIndex(0);
      setViewingList(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [audience, brief]);

  const answerQuestion = useCallback(async (answer) => {
    track(EVENTS.QUESTION_ANSWER, { question_key: answer?.key, audience });
    // Advance immediately. §5 — an answer is optional context, so waiting on
    // the round-trip would make an optional thing feel like a gate.
    setQuestionIndex((i) => i + 1);
    try {
      await api.saveCampaignAnswer(token, answer);
    } catch {
      /* Losing an optional answer is not worth interrupting the wait for. */
    }
  }, [token]);

  const retry = useCallback(async () => {
    track(EVENTS.LIST_RETRY, { audience, attempt: (visit?.attempts ?? 0) + 1 });
    setRetrying(true);
    try {
      await api.retryCampaignFirstList(token);
      setVisit((v) => ({ ...v, state: 'building' }));
      poll(token);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setRetrying(false);
    }
  }, [token, poll]);

  /**
   * §3 — the report's own poll. Separate from the visit poll because the two
   * answer different questions and settle at different times: the list is
   * done long before the research is.
   */
  const pollReport = useCallback(async (reportId) => {
    if (cancelled.current || !reportId) return;
    watchedReport.current = reportId;
    try {
      const next = await api.getCampaignReport(token, reportId);
      // Not just `cancelled`: the visitor may have gone back to the list
      // while this request was in flight, and applying its result would
      // drag them onto the research wait again.
      if (cancelled.current || watchedReport.current !== reportId) return;
      setActiveReport(next);
      if (!['ready', 'failed'].includes(next.status)) {
        reportTimer.current = setTimeout(() => pollReport(reportId), POLL_MS);
      }
    } catch {
      if (cancelled.current || watchedReport.current !== reportId) return;
      reportTimer.current = setTimeout(() => pollReport(reportId), POLL_MS * 2);
    }
  }, [token]);

  /**
   * §3/§8 — "Returning to the list should show an in-progress marker on that
   * card, not start a duplicate job." The server is idempotent per story, so a
   * second click returns the first job rather than creating another.
   */
  const requestReport = useCallback(async (opportunity) => {
    // Position, never the headline — §PII in campaignAnalytics.
    track(EVENTS.REPORT_REQUEST, { audience, position: opportunity?.position });
    setError(null);
    try {
      const out = await api.startCampaignReport(token, opportunity.storyId);
      setActiveReport({
        reportId: out.reportId,
        storyId: opportunity.storyId,
        storyTitle: opportunity.headline,
        status: out.status,
        result: null,
      });
      setViewingList(false);
      setReportOpen(false);
      pollReport(out.reportId);
    } catch (err) {
      const code = err.response?.data?.code;
      // Q14/Q17 — neither of these is an error to apologise for. Both have
      // approved prompts, and the code is what chooses between them.
      if (code === 'CAMPAIGN_ALLOWANCE_EXHAUSTED') {
        setUpgradePrompt('allowance');
        track(EVENTS.UPGRADE_PROMPT, { reason: 'allowance', audience });
      } else if (code === 'CAMPAIGN_ACCESS_ENDED') {
        setUpgradePrompt('expired');
        track(EVENTS.UPGRADE_PROMPT, { reason: 'expired', audience });
      }
      else setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  }, [token, pollReport]);

  const retryReport = useCallback(async () => {
    if (!activeReport?.reportId) return;
    try {
      await api.retryCampaignReport(token, activeReport.reportId);
      setActiveReport((r) => ({ ...r, status: 'regenerating' }));
      pollReport(activeReport.reportId);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    }
  }, [token, activeReport, pollReport]);

  const saveReport = useCallback(
    (payload) => api.saveCampaignReport(token, activeReport.reportId, payload),
    [token, activeReport],
  );

  const backToList = useCallback(() => {
    setError(null);
    // Stop WATCHING the report. The job itself keeps running — §3: "The job
    // continues if they browse the list or explore the platform
    // information" — but the poll has to let go, and this is why Back
    // looked broken on the research wait: clearing `activeReport` was not
    // enough, because the in-flight `setTimeout` fired a moment later and
    // `setActiveReport(next)` put the visitor straight back on the screen
    // they had just left. The timer is cleared and the id is forgotten, so
    // a late response is dropped rather than re-rendering the wait.
    if (reportTimer.current) clearTimeout(reportTimer.current);
    reportTimer.current = null;
    watchedReport.current = null;
    setReportOpen(false);
    setActiveReport(null);
    setViewingList(true);
    setHasSeenList(true);
  }, []);

  const loadWorkspace = useCallback(async () => {
    try {
      setWorkspace(await api.getCampaignWorkspace(token));
    } catch { /* the screen keeps whatever it already had */ }
  }, [token]);

  /**
   * Q6 — register, and carry on in the same place. No confirmation screen and
   * no email to complete: the workspace opens straight away, populated.
   */
  const doEnroll = useCallback(async (payload) => {
    setEnrolling(true);
    setError(null);
    // The address itself never reaches the dataLayer — `scrub()` would drop
    // it anyway, but it is not passed in the first place.
    track(EVENTS.ENROLL_SUBMIT, { audience });
    try {
      await api.enrollCampaignVisit(token, payload);
      track(EVENTS.ENROLL_SUCCESS, { audience });
      await loadWorkspace();
      setReportOpen(false);
      setActiveReport(null);
      setViewingList(false);
      setViewingWorkspace(true);
    } catch (err) {
      setError(err.response?.data?.error || 'We could not register that address. Please try again.');
    } finally {
      setEnrolling(false);
    }
  }, [token, loadWorkspace]);

  const openWorkspace = useCallback(async () => {
    await loadWorkspace();
    setReportOpen(false);
    setActiveReport(null);
    setViewingList(false);
    setViewingWorkspace(true);
  }, [loadWorkspace]);

  /**
   * Q13 — open any dated list, including an older one. It replaces the
   * first-list view rather than opening beside it, so there is only ever one
   * list on screen.
   */
  const openList = useCallback(async (listId) => {
    try {
      const list = await api.getCampaignList(token, listId);
      setVisit((v) => ({ ...v, opportunities: list.opportunities }));
      setViewingWorkspace(false);
      setViewingList(true);
      setHasSeenList(true);
    } catch (err) {
      setError(err.response?.data?.error || 'We could not open that list.');
    }
  }, [token]);

  const openSavedReport = useCallback(async (reportId) => {
    try {
      const report = await api.getCampaignReport(token, reportId);
      setActiveReport(report);
      setReportOpen(true);
      setViewingWorkspace(false);
    } catch (err) {
      setError(err.response?.data?.error || 'We could not open that research.');
    }
  }, [token]);

  /**
   * §2 — an empty result "offers a way to revise it". A revision is a new
   * brief, so it is a new visit: re-running the same words against the same
   * index returns the same nothing.
   */
  const revise = useCallback(() => {
    writeStored(tokenKeyFor(audience), null);
    setToken(null);
    setVisit(null);
    setViewingList(false);
    setHasSeenList(false);
    setActiveReport(null);
    setReportOpen(false);
    setQuestionIndex(0);
  }, [audience]);

  // Which screen the visitor is actually looking at.
  //
  // Computed here, BEFORE the early returns, because the effect below is a
  // hook and hooks cannot sit after a conditional return. It deliberately
  // reads the raw state rather than the `show*` booleans further down — those
  // are declared after the returns, and a dependency array referencing them
  // would hit the temporal dead zone.
  const stage = !token ? 'landing'
    : (viewingWorkspace && workspace) ? 'workspace'
      : (activeReport && reportOpen && activeReport.status === 'ready') ? 'report'
        : activeReport ? 'report_wait'
          : (visit?.state === 'ready' && viewingList) ? 'list'
            : visit?.state === 'empty' ? 'list_empty'
              : visit?.state === 'failed' ? 'list_failed'
                : visit?.state === 'delayed' ? 'waiting_delayed'
                  : 'waiting';

  // One event per stage transition. Keyed on the stage string, so a re-render
  // that changes nothing visible does not re-count the visitor. Gated on the
  // kill switch: a closed page is not a page view of the funnel.
  useEffect(() => {
    if (!audience) return;
    if (config.__loaded !== true || config.CAMPAIGN_EXPERIENCE_ENABLED !== true) return;
    trackStage(stage, audience);
  }, [stage, audience, config.__loaded, config.CAMPAIGN_EXPERIENCE_ENABLED]);

  // Every stage is a new page as far as the visitor is concerned, but it is
  // one route, so the browser never resets the scroll. Opening a report
  // from halfway down a long list used to land you halfway down the report.
  //
  // The campaign pages scroll inside `.mk` / `.cmp-page`, not on <body> —
  // the app's global CSS pins html and body — so `window.scrollTo` does
  // nothing here and the container has to be found.
  useEffect(() => {
    const scroller = document.querySelector('.mk, .cmp-page');
    if (!scroller) return;
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    // `scrollTo` is not universal on elements — jsdom has no implementation
    // at all — and a missing scroll reset must not take the page down with
    // it. Setting scrollTop is the fallback every engine has.
    if (typeof scroller.scrollTo === 'function') {
      scroller.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      scroller.scrollTop = 0;
    }
  }, [stage]);

  if (!audience) return null;

  // Wait for the config before deciding anything. `__loaded` distinguishes
  // "not fetched yet" from "fetched and off" — without it the closed notice
  // renders for a moment on every load, so the first thing a visitor from an
  // ad sees is "this page is not available".
  if (config.__loaded !== true) {
    // `hideCta` matters here as much as the blank body: the closing band
    // carries a heading and a "Request a demo" button, so without it the
    // page is no longer silent while we are still finding out whether the
    // funnel is open at all.
    //
    // `plain` matters for a different reason: this branch renders for the
    // whole of the /frontend-config fetch, and without it the shell fell
    // back to the FULL four-column footer and then collapsed to one line
    // the moment the config landed. That was the flicker — not an animation
    // or a late stylesheet, just the wrong variant for a few hundred ms.
    // The variant is resolved synchronously from the URL and localStorage,
    // so it is known here even though nothing else is.
    return (
      <Shell audience={audience} hideCta>
        <div className={C.closed} />
      </Shell>
    );
  }

  // The kill switch. Fails closed — `FrontendConfigContext` falls back to its
  // defaults when /frontend-config cannot be reached, and that default is off.
  if (config.CAMPAIGN_EXPERIENCE_ENABLED !== true) {
    return (
      // Closed means closed. Soliciting a demo underneath a notice that the
      // page is unavailable is the wrong thing to say, and the switch is
      // also what we reach for during an outage behind it.
      <Shell hideCta>
        <div className={C.closed}>
          This page is not available right now.
          <br />Please check back later.
        </div>
      </Shell>
    );
  }

  const state = visit?.state || null;
  const opportunities = visit?.opportunities || [];
  const showWorkspace = viewingWorkspace && Boolean(workspace);
  const showReport = !showWorkspace && Boolean(activeReport) && reportOpen && activeReport.status === 'ready';
  const showReportWait = !showWorkspace && Boolean(activeReport) && !showReport;
  const showList = !showWorkspace && state === 'ready' && viewingList && !activeReport;
  // §4 — registration sits BESIDE the first report, on the same screen, and
  // only once there is a report to sit beside.
  const showEnroll = showReport && !visit?.access?.enrolled;
  // §3 — an in-progress marker on the card, from the visit's own report list.
  const busyStoryId = (visit?.reports || [])
    .filter((r) => !['ready', 'failed'].includes(r.status))
    .map((r) => r.storyId)[0] || null;

  return (
    <Shell
      // §4 — FAQ and Pricing stay discoverable "throughout the journey". The
      // funnel used to omit the audience, so the two links existed on the
      // standalone pages and nowhere else: the header lost them the moment
      // anyone actually started.
      audience={audience}
      // The results pitch belongs on the landing screen only — someone who
      // has already submitted a brief is past being sold to. The closing CTA
      // band is unconditional and lives in the shell.
      showResults={!token}
      onRequestDemo={() => { window.location.href = `/c/${audience}/demo`; }}
      // Suppressed while a report is on screen: that step carries its own
      // prominent "Back to opportunities", and two controls doing the same
      // thing on one screen is a choice the visitor should not have to make.
      onBackToList={
        visit?.access?.enrolled && !showWorkspace
          ? openWorkspace
          : (hasSeenList && !showList && !activeReport ? () => setViewingList(true) : null)
      }
    >
      {!token && (
        <>
          <Landing
            audience={audience}
            brief={brief}
            onBriefChange={setBrief}
            onSubmit={submitBrief}
            error={error}
            submitting={submitting}
          />
          {!mock && <LandingCapabilities audience={audience} />}
        </>
      )}

      {token && !showList && !activeReport && !showWorkspace && (
        <Waiting
          audience={audience}
          brief={visit?.brief || brief}
          state={state}
          questionIndex={questionIndex}
          onAnswer={answerQuestion}
          onSkip={() => setQuestionIndex((i) => i + 1)}
          onOpenList={() => { setViewingList(true); setHasSeenList(true); }}
          onRetry={retry}
          retrying={retrying}
          attemptsRemaining={visit?.attemptsRemaining ?? 2}
          scope={visit?.scope || null}
          // Or's design puts the revision beside the notice that explains
          // why it is needed; the other design keeps it in the block below,
          // so only one of the two ever renders it.
          onRevise={mock ? revise : undefined}
        />
      )}

      {showList && (
        <List
          audience={audience}
          opportunities={opportunities}
          onRevise={revise}
          onRequestReport={requestReport}
          busyStoryId={busyStoryId}
          error={error}
        />
      )}

      {showReportWait && (
        <ReportWait
          audience={audience}
          storyTitle={activeReport.storyTitle}
          status={activeReport.status}
          onOpenReport={() => setReportOpen(true)}
          onBackToList={backToList}
          onRetry={retryReport}
          // §3 — the demo request is reachable from the report wait, and the
          // running job survives the navigation: it lives on the server, and
          // the session token brings the visitor back to it.
          onRequestDemo={() => { window.location.href = `/c/${audience}/demo`; }}
        />
      )}

      {/* ReportView owns the two-column grid, because the actions row it
          renders has to span both columns. The wrapper here used to hard-code
          `cmp-split`, which is the other design's class — in Or's skin it was
          simply unstyled. */}
      {showReport && (
        <ReportView
          report={activeReport}
          onBackToList={backToList}
          onSave={saveReport}
          skin={mock ? 'mk' : 'cmp'}
          aside={showEnroll ? (
            <Enroll
              audience={audience}
              onEnroll={doEnroll}
              submitting={enrolling}
              error={error}
            />
          ) : null}
        />
      )}

      {showWorkspace && (
        <Workspace
          audience={audience}
          workspace={workspace}
          onOpenList={openList}
          onOpenReport={openSavedReport}
          // Q12 — the upgrade destination is deferred, so no button until
          // there is somewhere for it to go.
          onUpgrade={null}
        />
      )}

      {/* Q14/Q17 — the two approved prompts. Shown in place rather than as a
          modal: both are answers to something the visitor just tried to do. */}
      {upgradePrompt && (
        <div className={C.notice}>
          <div>
            <strong className={C.noticeTitle}>
              {upgradePrompt === 'allowance' ? SHARED.allowanceTitle : SHARED.expiredTitle}
            </strong>
            <p className={C.noticeBody}>
              {upgradePrompt === 'allowance' ? SHARED.allowanceBody : SHARED.expiredBody}
            </p>
            <div className={C.actions}>
              <button
                type="button"
                className={C.quietBtn}
                onClick={() => setUpgradePrompt(null)}
              >
                {SHARED.notNow}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* §2 — an empty list is reachable without ever opening the list screen,
          because the waiting screen is where the visitor is when it lands.
          In Or's design the waiting screen carries this itself. */}
      {!mock && token && state === 'empty' && !showList && (
        <div className={C.actions}>
          <button type="button" className={C.primaryBtn} onClick={revise}>
            {copyFor(audience).primaryAction}
          </button>
        </div>
      )}
    </Shell>
  );
}
